import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { ActionButton, PageLayout } from '../../components/DesignSystem';
import { Card, Input, Select, Space, Button, message, Modal, Table, Spin } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { apiFetch } from '../../utils/apiFetch';
import './SupplierList.css';

const resolveSupplierImageSrc = (image: string): string => {
  if (image.startsWith('data:') || image.startsWith('http://') || image.startsWith('https://') || image.startsWith('//')) {
    return image;
  }
  return `${API_URL.replace('/api', '')}${image.startsWith('/') ? image : `/${image}`}`;
};

const SupplierAvatar: React.FC<{ image?: string | null; name?: string; supplierId?: string | number; hasLogo?: boolean }> = ({ image, name, supplierId, hasLogo }) => {
  const [errored, setErrored] = useState(false);
  const [lazySrc, setLazySrc] = useState<string | null>(null);
  const initial = (name || '?')[0].toUpperCase();
  const { token } = useAuth();

  useEffect(() => {
    if (image || !hasLogo || !supplierId || !token) return;
    let cancelled = false;
    apiFetch(`${API_URL}/suppliers/${supplierId}/logo`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data?.image) setLazySrc(data.image); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [image, hasLogo, supplierId, token]);

  const resolved = image || lazySrc;
  if (resolved && !errored) {
    const src = resolveSupplierImageSrc(resolved);
    return (
      <img
        src={src}
        alt={name || 'Supplier'}
        className="sup-avatar"
        onError={() => setErrored(true)}
      />
    );
  }

  return <div className="sup-avatar-fallback">{initial}</div>;
};

const LIST_PAGE_SIZE = 10;
const SUPPLIER_LIST_CACHE_KEY = 'shaho_supplier_list_v1';
const SUPPLIER_LIST_CACHE_MS = 5 * 60 * 1000;

function loadSupplierListCache(): any[] | null {
  try {
    const raw = sessionStorage.getItem(SUPPLIER_LIST_CACHE_KEY);
    if (!raw) return null;
    const { at, rows } = JSON.parse(raw);
    if (Date.now() - at > SUPPLIER_LIST_CACHE_MS || !Array.isArray(rows)) return null;
    return rows;
  } catch {
    return null;
  }
}

function saveSupplierListCache(rows: any[]) {
  try {
    sessionStorage.setItem(SUPPLIER_LIST_CACHE_KEY, JSON.stringify({ at: Date.now(), rows }));
  } catch { /* quota */ }
}

function mapSupplierRow(s: any) {
  return {
    ...s,
    id: s.id || s._id,
    contactPerson: s.contactPerson || s.contact_person || '',
    paymentTerms: s.paymentTerms || s.payment_terms || 'Net 30',
    preferredSupplier: !!(s.preferredSupplier || s.preferred_supplier),
    leadTimeDays: Number(s.leadTimeDays ?? s.lead_time_days ?? s.leadTime ?? s.lead_time ?? 0),
    totalPurchased: Number(s.totalPurchased ?? s.total_purchased ?? 0),
    outstandingPayable: Number(s.outstandingPayable ?? s.outstanding_payable ?? 0),
    lastPoDate: s.lastPoDate || s.last_po_date || null,
    image: s.image || s.logo || null,
    hasLogo: !!(s.has_logo || s.hasLogo || s.image || s.logo),
  };
}

const SupplierList: React.FC = () => {
  const initialCache = loadSupplierListCache();
  const [suppliers, setSuppliers] = useState<any[]>(initialCache ?? []);
  const [listPage, setListPage] = useState(1);
  const [openPoValue, setOpenPoValue] = useState(0);
  const [tableLoading, setTableLoading] = useState(!initialCache?.length);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const csvImportRef = useRef<HTMLInputElement>(null);
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [csvImportRows, setCsvImportRows] = useState<any[]>([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportErrors, setCsvImportErrors] = useState<Array<{ row: number; error: string }>>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  useEffect(() => {
    const flash = (location.state as { flash?: string })?.flash;
    if (flash) {
      message.success(flash);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    fetchSuppliers({ silent: !!initialCache?.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    setListPage(1);
  }, [searchTerm, statusFilter]);

  const fetchOpenPoSummary = async () => {
    try {
      if (!token) return;
      setSummaryLoading(true);
      const summaryRes = await apiFetch(`${API_URL}/purchase-orders/open-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (summaryRes.ok) {
        const summary = await summaryRes.json();
        setOpenPoValue(Number(summary.open_po_value || 0));
      }
    } catch {
      // non-blocking — stats can stay at 0
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchSuppliers = async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? suppliers.length > 0;
    try {
      if (!token) { navigate('/login'); return; }
      if (!silent) setTableLoading(true);
      const supRes = await apiFetch(
        `${API_URL}/suppliers?include_archived=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!supRes.ok) throw new Error(`Status ${supRes.status}`);
      const data = await supRes.json();
      const list: any[] = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : (Array.isArray(data?.suppliers) ? data.suppliers : Object.values(data || {})));
      const mapped = list.map(mapSupplierRow);
      setSuppliers(mapped);
      saveSupplierListCache(mapped);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      if (!silent) {
        setSuppliers([]);
        message.error('Failed to load suppliers');
      }
    } finally {
      if (!silent) setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchOpenPoSummary();
  }, [token]);

  const archiveSupplier = async (id: string, name: string) => {
    const reason = window.prompt(`Archive reason for "${name}"?\n(e.g. Switched supplier, Out of business)`);
    if (!reason) return;
    try {
      if (!token) { navigate('/login'); return; }
      const res = await apiFetch(`${API_URL}/suppliers/${id}/archive`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      if (res.ok) { message.success('Supplier archived'); fetchSuppliers(); }
      else { const e = await res.json(); message.error(e.message || 'Archive failed'); }
    } catch { message.error('Network error'); }
  };

  const restoreSupplier = async (id: string, name: string) => {
    if (!window.confirm(`Restore "${name}"?`)) return;
    try {
      if (!token) { navigate('/login'); return; }
      const res = await apiFetch(`${API_URL}/suppliers/${id}/restore`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { message.success('Supplier restored'); fetchSuppliers(); }
      else { const e = await res.json(); message.error(e.message || 'Restore failed'); }
    } catch { message.error('Network error'); }
  };

  const downloadSupplierTemplate = () => {
    const headers = 'name,company,email,phone,contact_person,category,payment_terms,status,address,city,postcode,country,tax_id';
    const example = 'ABC Wholesale Ltd,ABC Foods Ltd,orders@abc.co.uk,+44 20 1234 5678,Jane Smith,Grocery & Food,Net 30,Active,Unit 4 Industrial Estate,London,SW1A 1AA,United Kingdom,GB123456789';
    const link = document.createElement('a');
    link.href = encodeURI(`data:text/csv;charset=utf-8,${headers}\n${example}`);
    link.download = 'supplier-import-template.csv';
    link.click();
  };

  const handleCsvImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text = (ev.target?.result as string) || '';
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { message.warning('CSV must have a header row + at least one data row'); return; }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      const rows = lines.slice(1).map(line => {
        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        const obj: Record<string, string> = {};
        headers.forEach((h, idx) => { obj[h] = cols[idx] || ''; });
        return obj;
      });
      setCsvImportRows(rows);
      setCsvImportErrors([]);
      setShowCsvImportModal(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const submitCsvSupplierImport = async () => {
    setCsvImporting(true);
    setCsvImportErrors([]);
    try {
      const res = await apiFetch(`${API_URL}/suppliers/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ suppliers: csvImportRows }),
      });
      const data = await res.json();
      if (res.ok) {
        setShowCsvImportModal(false);
        setCsvImportRows([]);
        message.success(`Imported ${data.imported} supplier${data.imported !== 1 ? 's' : ''} successfully`);
        fetchSuppliers();
      } else {
        setCsvImportErrors(data?.errors || [{ row: 0, error: data?.error || 'Import failed' }]);
      }
    } catch {
      setCsvImportErrors([{ row: 0, error: 'Network error' }]);
    } finally {
      setCsvImporting(false);
    }
  };

  const filteredSuppliers = suppliers.filter(s => {
    const lower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      s?.name?.toLowerCase().includes(lower) ||
      s?.company?.toLowerCase().includes(lower) ||
      s?.email?.toLowerCase().includes(lower) ||
      s?.category?.toLowerCase().includes(lower) ||
      s?.phone?.toLowerCase().includes(lower);
    if (!matchesSearch) return false;
    const isArchived = !!s.deleted_at;
    if (statusFilter === 'Archived') return isArchived;
    if (statusFilter === 'All') return true;
    if (statusFilter === 'Active') return !isArchived && (s.status === 'Active' || !s.status);
    if (statusFilter === 'Inactive') return !isArchived && s.status === 'Inactive';
    return !isArchived;
  });

  const supplierPages = Math.max(1, Math.ceil(filteredSuppliers.length / LIST_PAGE_SIZE));
  const safeListPage = Math.min(listPage, supplierPages);
  const paginatedSuppliers = filteredSuppliers.slice(
    (safeListPage - 1) * LIST_PAGE_SIZE,
    safeListPage * LIST_PAGE_SIZE
  );

  const renderSupplierActions = (supplier: any, sid: string | number) => {
    const id = String(sid);
    return (
    <div className="sup-row-actions">
      {supplier.deleted_at ? (
        <button type="button" className="sup-act sup-act--restore" onClick={() => restoreSupplier(id, supplier.name)}>Restore</button>
      ) : (
        <>
          <button type="button" className="sup-act sup-act--view" onClick={() => navigate(`/suppliers/view/${id}`)}>View</button>
          <button type="button" className="sup-act sup-act--edit" onClick={() => navigate(`/suppliers/edit/${id}`)}>Edit</button>
          <button type="button" className="sup-act sup-act--po" onClick={() => navigate(`/purchase-orders?supplier_id=${id}`)}>New PO</button>
          <button type="button" className="sup-act sup-act--archive" onClick={() => archiveSupplier(id, supplier.name)}>Archive</button>
        </>
      )}
    </div>
    );
  };

  const activeCount   = suppliers.filter(s => !s.deleted_at && (s.status === 'Active' || !s.status)).length;
  const archivedCount = suppliers.filter(s => !!s.deleted_at).length;

  return (
    <PageLayout
      title="🏭 Supplier Management"
      action={<ActionButton variant="add" label="Add Supplier" onClick={() => navigate('/suppliers/add')} />}
    >
      <div className="sup-stats">
        <div className="sup-stat">
          <div className="sup-stat-label">Total Suppliers</div>
          <div className="sup-stat-value">{suppliers.filter(x => !x.deleted_at).length}</div>
        </div>
        <div className="sup-stat">
          <div className="sup-stat-label">Active</div>
          <div className="sup-stat-value" style={{ color: '#059669' }}>{activeCount}</div>
        </div>
        <div className="sup-stat">
          <div className="sup-stat-label">Archived</div>
          <div className="sup-stat-value" style={{ color: '#6b7280' }}>{archivedCount}</div>
        </div>
        <div className="sup-stat">
          <div className="sup-stat-label">Open PO Value</div>
          <div className="sup-stat-value">
            {summaryLoading ? '…' : `£${openPoValue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`}
          </div>
        </div>
      </div>

      <Card>
        {/* Filter bar */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <Space wrap>
            <Input
              placeholder="Search by name, company, email, category..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 340 }}
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 160 }}
            >
              <Select.Option value="Active">Active</Select.Option>
              <Select.Option value="Inactive">Inactive</Select.Option>
              <Select.Option value="All">All Suppliers</Select.Option>
              <Select.Option value="Archived">Archived</Select.Option>
            </Select>
          </Space>

          <Space>
            <input ref={csvImportRef} type="file" accept=".csv" onChange={handleCsvImportFile} style={{ display: 'none' }} />
            <Button icon={<DownloadOutlined />} onClick={downloadSupplierTemplate}>Template</Button>
            <Button onClick={() => csvImportRef.current?.click()}>📥 Import CSV</Button>
            {(searchTerm || statusFilter !== 'Active') && (
              <Button onClick={() => { setSearchTerm(''); setStatusFilter('Active'); }}>
                Clear Filters
              </Button>
            )}
          </Space>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
            {filteredSuppliers.length !== suppliers.length
              ? `${filteredSuppliers.length} of ${suppliers.length} suppliers`
              : `${suppliers.length} supplier${suppliers.length !== 1 ? 's' : ''}`}
          </span>
          {supplierPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button disabled={safeListPage <= 1} onClick={() => setListPage(p => Math.max(1, p - 1))}
                style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: safeListPage <= 1 ? '#f3f4f6' : '#fff', cursor: safeListPage <= 1 ? 'not-allowed' : 'pointer', fontSize: 13 }}>
                ←
              </button>
              <span style={{ fontSize: 13, color: '#374151' }}>{safeListPage} / {supplierPages}</span>
              <button disabled={safeListPage >= supplierPages} onClick={() => setListPage(p => Math.min(supplierPages, p + 1))}
                style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: safeListPage >= supplierPages ? '#f3f4f6' : '#fff', cursor: safeListPage >= supplierPages ? 'not-allowed' : 'pointer', fontSize: 13 }}>
                →
              </button>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{LIST_PAGE_SIZE} / page</span>
            </div>
          )}
        </div>

        <div className="sup-table-panel">
        <div className="sup-table-wrap">
          {tableLoading ? (
            <div className="sup-table-loading"><Spin /> Loading suppliers…</div>
          ) : (
          <table className="sup-table">
            <thead>
              <tr>
                <th style={{ width: '24%' }}>Supplier</th>
                <th style={{ width: '18%' }}>Contact</th>
                <th style={{ width: '11%' }}>Terms</th>
                <th style={{ width: '12%' }}>Outstanding</th>
                <th style={{ width: '12%' }}>Total Purchased</th>
                <th style={{ width: '9%' }}>Status</th>
                <th className="sup-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSuppliers.length > 0 ? paginatedSuppliers.map(supplier => {
                const sid = supplier.id || supplier._id;
                const isArchived = !!supplier.deleted_at;

                return (
                  <tr key={sid}>
                    <td>
                      <div className="sup-supplier-cell">
                        <SupplierAvatar image={supplier.image} name={supplier.name} supplierId={sid} hasLogo={supplier.hasLogo} />
                        <div style={{ minWidth: 0 }}>
                          <div className="sup-name">{supplier.name}</div>
                          <div className="sup-meta">
                            {[supplier.company, supplier.category].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="sup-contact">{supplier.contactPerson || supplier.email || '—'}</div>
                      <div className="sup-contact-sub">{supplier.phone || supplier.email || ''}</div>
                    </td>
                    <td>
                      <div>{supplier.paymentTerms || 'Net 30'}</div>
                      {Number(supplier.leadTimeDays || 0) > 0 && (
                        <div className="sup-meta">{Number(supplier.leadTimeDays)}d lead</div>
                      )}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      £{Number(supplier.outstandingPayable || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                    </td>
                    <td>
                      £{Number(supplier.totalPurchased || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                    </td>
                    <td>
                      {isArchived ? (
                        <span className="sup-status" style={{ background: '#6b7280' }}>Archived</span>
                      ) : (
                        <span className="sup-status" style={{
                          background:
                            supplier.status === 'Active' || !supplier.status ? '#059669' :
                            supplier.status === 'Inactive' ? '#d97706' : '#dc2626'
                        }}>
                          {supplier.status || 'Active'}
                        </span>
                      )}
                    </td>
                    <td className="sup-col-actions">
                      {renderSupplierActions(supplier, sid)}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    {suppliers.length === 0
                      ? '📭 No suppliers found. Add your first supplier!'
                      : '🔍 No suppliers match your filters. Try adjusting your search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          )}
        </div>
        </div>

        <div className="sup-mobile-cards">
          {tableLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /> Loading suppliers…</div>
          ) : filteredSuppliers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              {suppliers.length === 0
                ? '📭 No suppliers found. Add your first supplier!'
                : '🔍 No suppliers match your filters. Try adjusting your search.'}
            </div>
          ) : (
            paginatedSuppliers.map(supplier => {
              const sid = supplier.id || supplier._id;
              const isArchived = !!supplier.deleted_at;

              return (
                <div key={sid} className="sup-mobile-card">
                  <div className="sup-mobile-card-header">
                    <SupplierAvatar image={supplier.image} name={supplier.name} supplierId={sid} hasLogo={supplier.hasLogo} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="sup-mobile-card-name">{supplier.name}</div>
                      <div className="sup-meta">
                        {[supplier.company, supplier.category].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </div>
                  </div>

                  <div className="sup-mobile-card-info">
                    <div>
                      <div className="sup-mobile-card-label">Contact</div>
                      {supplier.contactPerson || supplier.email || '—'}
                    </div>
                    <div>
                      <div className="sup-mobile-card-label">Phone</div>
                      {supplier.phone || '—'}
                    </div>
                    <div>
                      <div className="sup-mobile-card-label">Terms</div>
                      {supplier.paymentTerms || 'Net 30'}
                    </div>
                    <div>
                      <div className="sup-mobile-card-label">Outstanding</div>
                      £{Number(supplier.outstandingPayable || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                    </div>
                    <div>
                      <div className="sup-mobile-card-label">Purchased</div>
                      £{Number(supplier.totalPurchased || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                    </div>
                    <div>
                      <div className="sup-mobile-card-label">Status</div>
                      {isArchived ? 'Archived' : (supplier.status || 'Active')}
                    </div>
                  </div>

                  <div className="sup-mobile-actions">
                    {renderSupplierActions(supplier, sid)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>
      <Modal
        title="Import Suppliers from CSV"
        open={showCsvImportModal}
        onOk={submitCsvSupplierImport}
        onCancel={() => setShowCsvImportModal(false)}
        okText={csvImporting ? 'Importing…' : 'Import'}
        confirmLoading={csvImporting}
        width={720}
      >
        {csvImportErrors.length > 0 && (
          <div style={{ marginBottom: 12, padding: 12, background: '#fef2f2', borderRadius: 8 }}>
            <strong style={{ color: '#dc2626' }}>Errors ({csvImportErrors.length}):</strong>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {csvImportErrors.map((e, i) => (
                <li key={i} style={{ fontSize: 13 }}>Row {e.row}: {e.error}</li>
              ))}
            </ul>
          </div>
        )}
        <Table
          size="small"
          pagination={false}
          scroll={{ x: true }}
          dataSource={csvImportRows.slice(0, 50).map((r, i) => ({ ...r, key: i }))}
          columns={[
            { title: 'Name', dataIndex: 'name', key: 'name' },
            { title: 'Company', dataIndex: 'company', key: 'company' },
            { title: 'Email', dataIndex: 'email', key: 'email' },
            { title: 'Category', dataIndex: 'category', key: 'category' },
          ]}
        />
        {csvImportRows.length > 50 && (
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Showing first 50 of {csvImportRows.length} rows.</p>
        )}
      </Modal>
    </PageLayout>
  );
};

export default SupplierList;